import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Download, Workflow, TrendingUp, Map, Brain, Menu as MenuIcon,
  Sparkles, Target, Package, Shield, Settings, Users, MessageSquare, Calendar,
  type LucideIcon,
} from 'lucide-react';
import {
  useNavigation,
  useMenuData,
  getMenuIdByPath,
  getBucketByMenuId,
  type MenuId,
} from '@/store/navigation';
import { useSystemFeatures } from '@/hooks/useSystemFeatures';

/** 与 TopNavigation 同表：模块开关（关闭的模块不作为标签落点） */
const MENU_MODULE_MAP: Record<MenuId, string | null> = {
  workbench: 'workbench',
  ingest: null,
  pipeline: 'pipeline',
  ask: null,
  daily: null,
  community: null,
  settings: null,
  graph: null,
  cognitive: 'cognitive',
  emergence: 'emergence',
  attention: null,
  capsules: null,
  knowledge: null,
  'social-brain': 'social_brain',
  'embodied-cognition': 'embodied_cognition',
};

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard, Monitor: LayoutDashboard, Download, Sparkles, Target, Package,
  Shield, Settings, Brain, Users, Workflow, MessageSquare, Map, Calendar, TrendingUp,
};

const MAX_TABS = 4;

interface MobileTabBarProps {
  /** 「更多」落点：打开侧栏抽屉（全部模块与设置都在里面） */
  onMore: () => void;
}

/**
 * 手机端底部标签栏（10-18 新增）。
 *
 * 为什么不是「把顶栏桶塞进顶栏」：390px 宽下顶栏已放 Logo/搜索/模型/主题/用户，
 * 再塞 5 个桶必然挤压到不可点。底部栏按拇指区放**前 4 个一级桶**，其余收进「更多」抽屉。
 * 语义与顶栏一致（同 activeMenu / 同 moduleEnabled 过滤），不是另起一套导航。
 */
const MobileTabBar: FC<MobileTabBarProps> = ({ onMore }) => {
  const location = useLocation();
  const { activeMenu, setActiveMenu, toggleSubMenu } = useNavigation();
  const { menuData, topNavBuckets } = useMenuData();
  const { data: systemFeatures } = useSystemFeatures();

  const moduleEnabled = (menuId: MenuId) => {
    const key = MENU_MODULE_MAP[menuId];
    if (!key) return true;
    return systemFeatures?.modules?.[key] !== false;
  };

  const currentMenuId = getMenuIdByPath(location.pathname, menuData);
  const currentBucket = getBucketByMenuId(currentMenuId, topNavBuckets);
  const tabs = topNavBuckets.slice(0, MAX_TABS);

  const handleTap = (bucketId: string) => {
    const bucket = topNavBuckets.find((b) => b.id === bucketId);
    if (!bucket) return;
    const enabledIds = bucket.moduleIds.filter(moduleEnabled);
    const target = enabledIds[0] || bucket.primaryModuleId;
    if (activeMenu === target) toggleSubMenu();
    else setActiveMenu(target);
  };

  return (
    <nav
      aria-label="主模块"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border-light bg-bg-secondary/95 backdrop-blur"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-stretch">
        {tabs.map((bucket) => {
          const Icon = ICON_MAP[bucket.icon] || Brain;
          const active = currentBucket?.id === bucket.id;
          return (
            <button
              key={bucket.id}
              onClick={() => handleTap(bucket.id)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[52px] px-1 py-1.5 text-[10px] leading-none transition-colors ${
                active ? 'text-accent' : 'text-text-secondary active:bg-bg-tertiary'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="truncate max-w-full">{bucket.label}</span>
            </button>
          );
        })}
        <button
          onClick={onMore}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[52px] px-1 py-1.5 text-[10px] leading-none text-text-secondary active:bg-bg-tertiary transition-colors"
        >
          <MenuIcon className="w-5 h-5" />
          <span>更多</span>
        </button>
      </div>
    </nav>
  );
};

export default MobileTabBar;
